/** Chooser state machine (pure). Side effects (API calls, URL, timers) live in useChooser.
 *
 *  ← Next clip  : NEXT_CLIP   (current clip goes to `exclude`, the queue hands out the next one)
 *  Choose →     : TOGGLE      (focused / given template into the selection)
 *  Enter = OK   : SUBMIT → QUEUED | FAILED   (QUEUED moves on to the next clip: one clip, one template)
 *  Skip clip    : SKIPPED
 *  Undo (10 s)  : UNDONE      (the clip comes back pinned), UNDO_EXPIRED
 *  none left    : derived from the queue answer (see `phase`)
 */
export const UNDO_MS = 10_000;

/** The templates limited to one per clip (vp/render/service.py ONE_PER_CLIP). */
export const FOOTAGE: ReadonlySet<string> = new Set(["fall_gold", "blood", "orbit", "two", "script"]);

export type Undo =
  | { kind: "renders"; clipId: number; renderIds: number[]; until: number }
  | { kind: "skip"; clipId: number; version: number; until: number };

export type ChooserState = {
  exclude: number[];
  pinned: number | null;
  selected: string[];
  focus: number;
  submitting: boolean;
  undo: Undo | null;
  log: { queued: number[]; skipped: number[]; passed: number[] };
};

export type ChooserEvent =
  | { type: "NEXT_CLIP"; clipId: number }
  | { type: "TOGGLE"; templateId: string }
  | { type: "FOCUS"; index: number }
  | { type: "SUBMIT" }
  | { type: "QUEUED"; clipId: number; renderIds: number[]; now: number }
  | { type: "FAILED" }
  | { type: "SKIPPED"; clipId: number; version: number; now: number }
  | { type: "UNDONE" }
  | { type: "UNDO_EXPIRED"; now: number }
  | { type: "PIN"; clipId: number | null }
  | { type: "RESTART" };

export function initialChooser(exclude: number[] = [], pinned: number | null = null): ChooserState {
  return { exclude, pinned, selected: [], focus: 0, submitting: false, undo: null,
    log: { queued: [], skipped: [], passed: [] } };
}

const add = (xs: number[], x: number) => (xs.includes(x) ? xs : [...xs, x]);
const remove = (xs: number[], x: number) => xs.filter((y) => y !== x);

export function chooserReducer(s: ChooserState, e: ChooserEvent): ChooserState {
  switch (e.type) {
    case "NEXT_CLIP":
      if (s.submitting) return s;
      return { ...s, exclude: add(s.exclude, e.clipId), pinned: null, selected: [], focus: 0,
        log: { ...s.log, passed: add(s.log.passed, e.clipId) } };
    case "TOGGLE":
      if (s.submitting) return s;
      // one clip, one footage template (operator 2026-10-13): picking fall_gold / blood / orbit / two / script replaces
      // another of the five; other templates toggle freely
      if (s.selected.includes(e.templateId)) return { ...s, selected: s.selected.filter((t) => t !== e.templateId) };
      return { ...s, selected: [...s.selected.filter((t) => !(FOOTAGE.has(t) && FOOTAGE.has(e.templateId))), e.templateId] };
    case "FOCUS":
      return { ...s, focus: Math.max(0, e.index) };
    case "SUBMIT":
      if (s.submitting || s.selected.length === 0) return s;
      return { ...s, submitting: true };
    case "QUEUED":
      return { ...s, submitting: false, selected: [], focus: 0, pinned: null, exclude: add(s.exclude, e.clipId),
        undo: { kind: "renders", clipId: e.clipId, renderIds: e.renderIds, until: e.now + UNDO_MS },
        log: { ...s.log, queued: [...s.log.queued, ...e.renderIds.filter((r) => !s.log.queued.includes(r))] } };
    case "FAILED":
      return { ...s, submitting: false };
    case "SKIPPED":
      return { ...s, selected: [], focus: 0, pinned: null, exclude: add(s.exclude, e.clipId),
        undo: { kind: "skip", clipId: e.clipId, version: e.version, until: e.now + UNDO_MS },
        log: { ...s.log, skipped: add(s.log.skipped, e.clipId) } };
    case "UNDONE": {
      const u = s.undo;
      if (!u) return s;
      const log = u.kind === "renders"
        ? { ...s.log, queued: s.log.queued.filter((r) => !u.renderIds.includes(r)) }
        : { ...s.log, skipped: remove(s.log.skipped, u.clipId) };
      return { ...s, undo: null, pinned: u.clipId, exclude: remove(s.exclude, u.clipId), selected: [], focus: 0, log };
    }
    case "UNDO_EXPIRED":
      return s.undo && s.undo.until <= e.now ? { ...s, undo: null } : s;
    case "PIN":
      return { ...s, pinned: e.clipId, selected: [], focus: 0 };
    case "RESTART":
      return { ...s, exclude: [], pinned: null, selected: [], focus: 0 };
  }
}

export type Phase = "loading" | "ready" | "none_left" | "error";

export function phase(q: { pending: boolean; error: boolean; noneLeft: boolean; clipId: number | null }): Phase {
  if (q.error) return "error";
  if (q.pending) return "loading";
  if (q.noneLeft || q.clipId == null) return "none_left";
  return "ready";
}
